import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/* Iter98 + Iter99 — Public sector-stats trust widget.
   Anonymous, iframe-friendly, zero-PII. Drops into ANY third-party site
   as a live "Connexis powers the UK fintech lead market" trust badge.
   No token in URL, just a public GET to /api/embed/sector-stats.

   Iter99 query params:
     ?theme=dark         — dark mode
     ?theme=light        — light (default)
     ?accent=FF6B35      — custom accent color (hex without #, or with #)
     ?compact=1          — hide sector table, KPI strip only (240px tall)
     ?limit=5            — limit sector rows shown (default: all 10) */

const COMPACT_HEIGHT = 480;

// Parse accent — allow #abc, abc, abcdef, #abcdef. Strip anything dodgy.
function safeAccent(input) {
  if (!input) return null;
  const m = String(input).replace(/^#/, "").match(/^[0-9a-fA-F]{3,8}$/);
  return m ? `#${m[0]}` : null;
}

export default function EmbedSectorStats() {
  const [params] = useSearchParams();

  const theme   = (params.get("theme") || "light").toLowerCase();
  const compact = params.get("compact") === "1" || params.get("compact") === "true";
  const accent  = safeAccent(params.get("accent")) || "#0055FF";
  const limit   = Math.max(1, Math.min(20, Number(params.get("limit") || 20)));

  const isDark = theme === "dark";

  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/embed/sector-stats`)
        .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then((d) => { if (alive) setData(d); })
        .catch((e) => { if (alive) setErr(e.message || "Failed to load"); });
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const cls = useMemo(() => themeClasses(isDark), [isDark]);

  return (
    <div
      className={`${cls.root} min-h-[${compact ? 240 : 480}px]`}
      data-testid="embed-sector-stats"
      data-theme={isDark ? "dark" : "light"}
      style={{ "--connexis-accent": accent }}
    >
      <style>{`html, body, #root { background: ${isDark ? "#0a0f1c" : "#fff"}; margin:0; padding:0; }`}</style>

      {/* Top bar */}
      <div className={`px-5 py-3.5 border-b ${cls.border} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <ConnexisDot pulse accent={accent} />
          <div className={`text-[12.5px] font-semibold ${cls.titleText}`}>Connexis · Live fintech lead market</div>
        </div>
        <div className={`hidden sm:flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] ${cls.subtleText} font-semibold`}>
          <span className="relative inline-flex">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            <span className="absolute inset-0 size-1.5 rounded-full bg-emerald-500 animate-ping opacity-60" />
          </span>
          Last 24 hours
        </div>
      </div>

      {/* Error / loading */}
      {err && (
        <div className="p-10 text-center" data-testid="sector-stats-error">
          <div className="text-[13px] text-rose-500 font-semibold">Stats unavailable</div>
          <div className={`mt-1 text-[12px] ${cls.subtleText}`}>{err}</div>
        </div>
      )}

      {!err && !data && (
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sector-stats-loading">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={`h-24 rounded-lg ${cls.skel} animate-pulse`} />
          ))}
        </div>
      )}

      {data && (
        <div className="px-5 py-4" data-testid="sector-stats-body">
          {/* KPI strip — always shown */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat cls={cls} label="Leads · 24h"     value={data.total_leads_24h.toLocaleString()} testid="ss-kpi-leads" />
            <Stat cls={cls} label="Active publishers" value={data.active_publishers.toLocaleString()} testid="ss-kpi-publishers" />
            <Stat cls={cls} label="Fill rate"        value={data.overall_fill_pct !== null ? `${data.overall_fill_pct}%` : "—"} accentColor={fillColor(data.overall_fill_pct, isDark)} testid="ss-kpi-fill" />
          </div>

          {/* Sector grid — hidden in compact mode */}
          {!compact && (
            <div className={`rounded-lg border ${cls.border} overflow-hidden`} data-testid="embed-sector-table">
              <div className={`px-3 py-2 ${cls.tableHeaderBg} border-b ${cls.border} text-[10.5px] uppercase tracking-[0.16em] font-semibold ${cls.subtleText} flex`}>
                <div className="flex-1">Sector</div>
                <div className="w-24 text-right">Base price</div>
                <div className="w-20 text-right">24h</div>
                <div className="w-20 text-right">Fill</div>
              </div>
              <div className="max-h-[320px] overflow-y-auto">
                {data.sectors.slice(0, limit).map((s) => (
                  <div
                    key={s.slug}
                    className={`flex items-center px-3 py-2 border-b ${cls.borderRow} last:border-b-0 text-[12.5px] ${cls.rowHover} transition-colors`}
                    data-testid={`ss-row-${s.slug}`}
                  >
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="size-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span className={`${cls.rowText} truncate`}>{s.name}</span>
                    </div>
                    <div className={`w-24 text-right font-mono tabular-nums ${cls.rowTextStrong} font-semibold`}>£{s.base_price_gbp.toFixed(0)}</div>
                    <div className={`w-20 text-right font-mono tabular-nums ${cls.rowText}`}>{s.leads_24h}</div>
                    <div className={`w-20 text-right font-mono tabular-nums font-semibold ${fillColor(s.fill_rate_pct, isDark)}`}>
                      {s.fill_rate_pct !== null ? `${s.fill_rate_pct}%` : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className={`mt-3 flex items-center justify-between text-[10.5px] ${cls.subtleText}`}>
            <span>Updated {new Date(data.generated_at).toLocaleTimeString()}</span>
            <a
              href="https://connex.is"
              target="_blank"
              rel="noopener noreferrer"
              className={`font-semibold ${cls.footerLink} inline-flex items-center gap-1 transition-colors`}
              style={{ "--accent-hover": accent }}
              data-testid="ss-footer-link"
              onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "")}
            >
              Powered by Connexis
              <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ cls, label, value, accentColor, testid }) {
  return (
    <div className={`rounded-md border ${cls.border} p-2.5 ${cls.kpiBg}`} data-testid={testid}>
      <div className={`text-[10.5px] uppercase tracking-[0.16em] ${cls.subtleText} font-semibold leading-tight`}>{label}</div>
      <div className={`mt-1 text-[19px] font-semibold font-mono tabular-nums leading-none ${accentColor || cls.kpiValueText}`}>{value}</div>
    </div>
  );
}

function fillColor(pct, isDark) {
  if (pct === null || pct === undefined) return isDark ? "text-slate-500" : "text-slate-400";
  if (pct >= 90) return isDark ? "text-emerald-400" : "text-emerald-700";
  if (pct >= 70) return isDark ? "text-amber-400"   : "text-amber-700";
  return isDark ? "text-rose-400" : "text-rose-700";
}

function ConnexisDot({ pulse, accent }) {
  return (
    <span className="relative inline-flex">
      <span
        className="inline-block size-3 rounded-full"
        style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
      />
      {pulse && <span className="absolute inset-0 inline-block size-3 rounded-full animate-ping opacity-40" style={{ background: accent }} />}
    </span>
  );
}

// All theme-dependent classes in one place so the JSX stays readable.
function themeClasses(isDark) {
  if (isDark) {
    return {
      root:           "bg-[#0a0f1c] text-slate-100",
      border:         "border-slate-800",
      borderRow:      "border-slate-800/70",
      titleText:      "text-slate-100",
      subtleText:     "text-slate-400",
      tableHeaderBg:  "bg-slate-900/60",
      rowText:        "text-slate-300",
      rowTextStrong:  "text-slate-100",
      rowHover:       "hover:bg-slate-900/60",
      kpiBg:          "bg-slate-900/50",
      kpiValueText:   "text-slate-100",
      skel:           "bg-slate-800/60",
      footerLink:     "text-slate-400",
    };
  }
  return {
    root:           "bg-white text-slate-900",
    border:         "border-slate-200",
    borderRow:      "border-slate-100",
    titleText:      "text-slate-900",
    subtleText:     "text-slate-400",
    tableHeaderBg:  "bg-slate-50/60",
    rowText:        "text-slate-700",
    rowTextStrong:  "text-slate-900",
    rowHover:       "hover:bg-slate-50/50",
    kpiBg:          "bg-white",
    kpiValueText:   "text-slate-900",
    skel:           "bg-slate-100",
    footerLink:     "text-slate-600",
  };
}

EmbedSectorStats.COMPACT_HEIGHT = COMPACT_HEIGHT;
